import { Col, Row } from 'reactstrap';
import { useAppSelector, useAppDispatch } from '@reducers/index';
import { generateUrl } from '@routes/util';
import { currentInitiative } from '@selectors/initiative';
import Dashboard, { DashboardRow, DashboardSection } from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { AdminBreadcrumbsProps } from '@g17eco/molecules/breadcrumbs';
import { RouteInterface } from '@g17eco/types/routes';
import { OtherStockExchanges } from './OtherStockExchanges';
import { useGetListOfStockExchangesQuery, useUpdateExchangeSettingsMutation } from '@api/stock-exchange-settings';
import { categorizeStockExchangesByLocation } from '@utils/stock-exchange-settings';
import { getDefaultConfig } from '../../config/app-config';
import { PopularStockExchanges } from './PopularStockExchanges';
import { useEffect, useState } from 'react';
import { StockExchange } from '@g17eco/types/stock-exchange-settings';
import { toastFromError } from '@g17eco/molecules/toasts';
import { reloadInitiative } from '@actions/initiative';

interface Props {
  baseRoute: RouteInterface;
  BreadcrumbsComponent: (props: AdminBreadcrumbsProps) => JSX.Element;
}

const StockExchangeSettings = (props: Props) => {
  const location = getDefaultConfig().locationWhitelist ?? [];
  const dispatch = useAppDispatch();
  const initiative = useAppSelector(currentInitiative);
  const { data: listOfStockExchanges = [], isFetching } = useGetListOfStockExchangesQuery();
  const [updateStockExchangeSettings] = useUpdateExchangeSettingsMutation();

  const [selectedStockExchanges, setSelectedStockExchanges] = useState<StockExchange[]>([]);

  useEffect(() => {
    if (initiative?.exchangeSettings) {
      setSelectedStockExchanges(initiative.exchangeSettings);
    }
  }, [initiative?.exchangeSettings]);

  const isSelectedStockExchange = (stockExchange: StockExchange) => {
    return selectedStockExchanges.some((item) => item.code === stockExchange.code);
  };
  const handleSelectStockExchange = async (stockExchange: StockExchange) => {
    if (!initiative) {
      return;
    }

    const previous = selectedStockExchanges;
    const updated = isSelectedStockExchange(stockExchange)
      ? selectedStockExchanges.filter((item) => item.code !== stockExchange.code)
      : [...selectedStockExchanges, stockExchange];

    setSelectedStockExchanges(updated);
    try {
      await updateStockExchangeSettings({
        initiativeId: initiative._id,
        exchangeSettings: updated.map(({ name, code }) => ({ name, code })),
      }).unwrap();
      await dispatch(reloadInitiative());
    } catch (err) {
      setSelectedStockExchanges(previous);
      toastFromError({ error: err, title: 'Failed to update stock exchange settings' });
    }
  };

  if (!initiative || isFetching) {
    return <Loader />;
  }

  const { baseRoute, BreadcrumbsComponent } = props;
  const breadcrumbs = [
    { label: 'Company Settings', url: generateUrl(baseRoute, { initiativeId: initiative._id }) },
    { label: 'Stock Exchange Settings' },
  ];

  const { popularStockExchanges, otherStockExchanges } = categorizeStockExchangesByLocation(
    listOfStockExchanges,
    location,
  );

  return (
    <Dashboard className='stock-exchange-settings'>
      <DashboardRow>
        <BreadcrumbsComponent breadcrumbs={breadcrumbs} initiativeId={initiative?._id ?? ''} />
      </DashboardRow>
      <DashboardRow>
        <h3 className='pl-2'>Stock Exchange settings</h3>
      </DashboardRow>
      <DashboardSection>
        <Row className='justify-content-center'>
          <Col className='col-md-8 col-12'>
            <h3 className='text-strong text-ThemeTextDark'>Select your stock exchange</h3>
            {popularStockExchanges.length > 0 ? (
              <>
                <h4 className='text-ThemeTextDark mt-4'>Popular stock exchanges</h4>
                <PopularStockExchanges
                  popularStockExchanges={popularStockExchanges}
                  isSelectedStockExchange={isSelectedStockExchange}
                  handleSelectStockExchange={handleSelectStockExchange}
                />
              </>
            ) : null}
          </Col>
          <Col className='col-12 d-flex justify-content-center flex-wrap'>
            <OtherStockExchanges
              initiative={initiative}
              isLoading={false}
              otherStockExchanges={otherStockExchanges}
              isSelectedStockExchange={isSelectedStockExchange}
              handleSelectStockExchange={handleSelectStockExchange}
              onManualStockExchangeAdded={(stockExchange) => {
                setSelectedStockExchanges((prev) => [...prev, stockExchange]);
                dispatch(reloadInitiative());
              }}
            />
          </Col>
        </Row>
      </DashboardSection>
    </Dashboard>
  );
};

export default StockExchangeSettings;
